import { GoogleGenAI, LiveServerMessage, Modality, Type, FunctionDeclaration } from '@google/genai';
import { SYSTEM_INSTRUCTION, SERVICES_PRICING, getSystemInstruction } from '../constants';
import * as CalendarService from './mockCalendarService';
import { sendContactEmail } from './googleServices';

// Audio Utils for PCM
function floatTo16BitPCM(input: Float32Array): ArrayBuffer {
  const output = new DataView(new ArrayBuffer(input.length * 2));
  for (let i = 0; i < input.length; i++) {
    const s = Math.max(-1, Math.min(1, input[i]));
    output.setInt16(i * 2, s < 0 ? s * 0x8000 : s * 0x7FFF, true);
  }
  return output.buffer;
}

function base64ToUint8Array(base64: string): Uint8Array {
  const binaryString = atob(base64);
  const bytes = new Uint8Array(binaryString.length);
  for (let i = 0; i < binaryString.length; i++) {
    bytes[i] = binaryString.charCodeAt(i);
  }
  return bytes;
}

// Tool Definitions
export const tools: FunctionDeclaration[] = [
  {
    name: 'checkAvailability',
    description: 'Check available appointment slots for a specific date.',
    parameters: {
      type: Type.OBJECT,
      properties: {
        date: { type: Type.STRING, description: 'Date in YYYY-MM-DD format' }
      },
      required: ['date']
    }
  },
  {
    name: 'bookAppointment',
    description: 'Book a new appointment.',
    parameters: {
      type: Type.OBJECT,
      properties: {
        date: { type: Type.STRING, description: 'YYYY-MM-DD' },
        time: { type: Type.STRING, description: 'HH:mm' },
        patientName: { type: Type.STRING },
        reason: { type: Type.STRING, description: 'Reason for visit (Checkup, Pain, etc.)' },
        phone: { type: Type.STRING }
      },
      required: ['date', 'time', 'patientName', 'phone']
    }
  },
  {
    name: 'cancelAppointment',
    description: 'Cancel an existing appointment.',
    parameters: {
      type: Type.OBJECT,
      properties: {
        date: { type: Type.STRING },
        time: { type: Type.STRING }
      },
      required: ['date', 'time']
    }
  },
  {
    name: 'getPricing',
    description: 'Get pricing for a specific service.',
    parameters: {
      type: Type.OBJECT,
      properties: {
        serviceName: { type: Type.STRING, description: 'Name of the service' }
      },
      required: ['serviceName']
    }
  },
  {
    name: 'transferToEmergency',
    description: 'Simulate transferring the call to an emergency line.',
    parameters: {
      type: Type.OBJECT,
      properties: {},
    }
  },
  {
    name: 'sendConfirmation',
    description: 'Send an appointment confirmation via Email or SMS.',
    parameters: {
      type: Type.OBJECT,
      properties: {
        method: { type: Type.STRING, enum: ['email', 'sms'] },
        destination: { type: Type.STRING, description: 'Email address or phone number' },
        details: { type: Type.STRING, description: 'Appointment details summary' }
      },
      required: ['method', 'destination']
    }
  },
  {
    name: 'updatePreferences',
    description: 'Update user preferences for notifications.',
    parameters: {
      type: Type.OBJECT,
      properties: {
        optInSms: { type: Type.BOOLEAN },
        optInEmail: { type: Type.BOOLEAN },
        reminderTime: { type: Type.STRING, description: 'e.g., "24h before"' }
      }
    }
  },
  {
    name: 'submitInquiry',
    description: 'MANDATORY: Send user information to contact@codealignr.com. You MUST use this tool when: user shows interest in services, wants consultation, asks about pricing/getting started, or wants to be contacted. DO NOT just say you will send it - you MUST call this tool. Only claim to have sent information AFTER this tool returns success.',
    parameters: {
      type: Type.OBJECT,
      properties: {
        name: { type: Type.STRING, description: 'Full name of the person (REQUIRED)' },
        email: { type: Type.STRING, description: 'Email address (REQUIRED)' },
        phone: { type: Type.STRING, description: 'Phone number (optional but include if provided)' },
        message: { type: Type.STRING, description: 'Their message, questions, or what they are interested in (optional)' },
        interest: { type: Type.STRING, description: 'What they are interested in (e.g., AI Voice Agents, Chatbots, Consultation, Pricing) - include this if mentioned' },
        companyName: { type: Type.STRING, description: 'Company name (optional but include if provided)' },
        preferredContactTime: { type: Type.STRING, description: 'Preferred time to be contacted (optional but include if provided)' }
      },
      required: ['name', 'email']
    }
  }
];

// Shared Tool Execution Logic
export const runTool = async (name: string, args: any): Promise<{ result: any, log: string }> => {
  let result: any = { error: 'Unknown function' };
  let log = '';

  switch (name) {
    case 'checkAvailability':
      const slots = CalendarService.checkAvailability(args.date as string);
      result = { availableSlots: slots };
      log = `Checked schedule for ${args.date}: ${slots.length} slots found.`;
      break;
    case 'bookAppointment':
      const booking = CalendarService.bookAppointment(
        args.date as string, 
        args.time as string, 
        args.patientName as string, 
        args.reason as string, 
        args.phone as string
      );
      result = booking ? { status: 'success', details: booking } : { status: 'failed', reason: 'Slot unavailable' };
      log = booking ? `Booked for ${args.patientName}` : `Booking failed for ${args.time}`;
      break;
    case 'cancelAppointment':
      const cancelled = CalendarService.cancelAppointment(args.date as string, args.time as string);
      result = { status: cancelled ? 'success' : 'not_found' };
      log = cancelled ? 'Appointment cancelled' : 'Cancel failed - not found';
      break;
    case 'getPricing':
      const price = Object.entries(SERVICES_PRICING).find(([k]) => k.toLowerCase().includes((args.serviceName as string).toLowerCase()));
      result = { price: price ? price[1] : 'Please contact front desk for a quote.' };
      log = `Checked price for ${args.serviceName}: ${result.price}`;
      break;
    case 'transferToEmergency':
      result = { status: 'transferred', message: 'Call transferred to emergency line.' };
      log = 'URGENT: Transferring to emergency line...';
      break;
    case 'sendConfirmation':
      const { method, destination } = args as any;
      result = { status: 'sent', timestamp: new Date().toISOString() };
      log = `Sent ${method.toUpperCase()} confirmation to ${destination}`;
      break;
    case 'updatePreferences':
      result = { status: 'updated' };
      log = 'Patient notification preferences updated.';
      break;
    case 'submitInquiry':
      try {
        console.log('🔧 submitInquiry tool called with args:', args);
        const contactData = {
          name: args.name as string,
          email: args.email as string,
          phone: args.phone as string || '',
          message: args.message as string || '',
          interest: args.interest as string || '',
          companyName: args.companyName as string || '',
          preferredContactTime: args.preferredContactTime as string || ''
        };
        console.log('📋 Contact data prepared:', contactData);
        const emailResult = await sendContactEmail(contactData);
        console.log('📧 Email result:', emailResult);
        result = emailResult;
        if (emailResult.success) {
          log = `SUCCESS: Inquiry from ${args.name} (${args.email}) has been successfully sent to contact@codealignr.com. The email was delivered. You can now confirm this to the user.`;
        } else {
          log = `FAILED: Could not send inquiry from ${args.name}. The email was NOT sent. Do NOT tell the user that you sent their information. Instead, ask them to use the contact form on the website or email contact@codealignr.com directly.`;
        }
      } catch (error) {
        console.error('❌ Error in submitInquiry:', error);
        result = { success: false, error: 'Failed to send inquiry' };
        log = `Error sending inquiry: ${error}`;
      }
      break;
    default:
      log = `Unknown tool called: ${name}`;
  }

  return { result, log };
};

export class GeminiLiveClient {
  private ai: GoogleGenAI;
  private inputContext: AudioContext | null = null;
  private outputContext: AudioContext | null = null;
  private stream: MediaStream | null = null;
  private processor: ScriptProcessorNode | null = null;
  private source: MediaStreamAudioSourceNode | null = null;
  private nextStartTime = 0;
  private isConnected = false;
  private activeAudioSources: AudioBufferSourceNode[] = [];
  private session: any = null;
  private callEndTimer: NodeJS.Timeout | null = null;
  
  public onStatusChange: (status: string) => void = () => {};
  public onLogMessage: (source: 'user' | 'agent' | 'system', text: string) => void = () => {};
  public onAudioData: (amplitude: number) => void = () => {}; 
  public onToolCall: (name: string, args: any) => void = () => {}; 

  constructor() {
    this.ai = new GoogleGenAI({ apiKey: process.env.REACT_APP_GEMINI_API_KEY || '' });
  }

  async connect(useCaseId?: string) {
    if (this.isConnected) return;

    try {
      this.onStatusChange('connecting');
      const AudioContext = window.AudioContext || (window as any).webkitAudioContext;
      this.inputContext = new AudioContext({ sampleRate: 16000 });
      this.outputContext = new AudioContext({ sampleRate: 24000 });
      
      this.stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      
      // Get system instruction based on use case, default to AI Receptionist
      const systemInstruction = useCaseId ? getSystemInstruction(useCaseId) : SYSTEM_INSTRUCTION;
      
      const config = {
        model: 'gemini-2.5-flash-native-audio-preview-09-2025',
        systemInstruction: systemInstruction,
        tools: [{ functionDeclarations: tools }]
      };

      const sessionPromise = this.ai.live.connect({
        model: config.model,
        config: {
          responseModalities: [Modality.AUDIO],
          speechConfig: {
            voiceConfig: { prebuiltVoiceConfig: { voiceName: 'Kore' } }
          },
          systemInstruction: config.systemInstruction,
          tools: config.tools,
          inputAudioTranscription: {},
          outputAudioTranscription: {},
        },
        callbacks: {
          onopen: async () => {
            this.isConnected = true;
            this.session = await sessionPromise;
            
            // Resume audio context if suspended (required for autoplay policy)
            if (this.outputContext && this.outputContext.state === 'suspended') {
              await this.outputContext.resume();
              console.log('Audio context resumed');
            }
            
            this.setupAudioInput(sessionPromise);
            this.onLogMessage('system', 'Connected to MedVoice AI');
            
            // Reset call end timer
            this.resetCallEndTimer();
            
            // Trigger initial greeting - AI must speak first
            // Wait a moment for everything to be ready, then trigger the greeting
            setTimeout(async () => {
              try {
                // Ensure audio context is active
                if (this.outputContext && this.outputContext.state === 'suspended') {
                  await this.outputContext.resume();
                }
                
                // Send a prompt to trigger the first response
                // The system instruction explicitly tells the AI to speak first
                await this.session.sendRealtimeInput({
                  text: 'Hello'
                });
                console.log('Triggered initial greeting - AI should speak first');
                this.onStatusChange('speaking'); // Set status to speaking since AI is about to talk
              } catch (error) {
                console.error('Error triggering greeting:', error);
                // Try alternative: send empty text
                try {
                  await this.session.sendRealtimeInput({
                    text: ''
                  });
                } catch (e2) {
                  console.error('Error with empty text trigger:', e2);
                }
              }
            }, 800); // Longer delay to ensure connection and audio are fully ready
            
            // Set status to listening initially, will change to speaking when AI responds
            this.onStatusChange('listening');
          },
          onmessage: async (msg: LiveServerMessage) => {
            this.handleMessage(msg, sessionPromise);
          },
          onclose: () => {
            console.log('Connection closed');
            this.clearCallEndTimer();
            // Disconnect will handle status update and cleanup
            this.disconnect();
          },
          onerror: (err) => {
            console.error('Connection error:', err);
            this.onStatusChange('error');
            this.onLogMessage('system', `Connection error: ${err.message || 'Unknown error'}`);
            this.disconnect();
          }
        }
      });

    } catch (error) {
      console.error('Connection failed:', error);
      this.onStatusChange('error');
    }
  }

  private setupAudioInput(sessionPromise: Promise<any>) {
    if (!this.inputContext || !this.stream) return;

    this.source = this.inputContext.createMediaStreamSource(this.stream);
    this.processor = this.inputContext.createScriptProcessor(4096, 1, 1);

    this.processor.onaudioprocess = (e) => {
      const inputData = e.inputBuffer.getChannelData(0);
      
      let sum = 0;
      for(let i=0; i<inputData.length; i++) sum += inputData[i] * inputData[i];
      const rms = Math.sqrt(sum / inputData.length);
      this.onAudioData(rms);

      // Check if user is speaking (interruption detection)
      const isUserSpeaking = rms > 0.01; // Threshold for detecting speech
      if (isUserSpeaking && this.activeAudioSources.length > 0) {
        // User is interrupting - stop current audio playback
        this.interruptAudio();
        this.resetCallEndTimer();
      }

      const pcm16 = floatTo16BitPCM(inputData);
      
      let binary = '';
      const bytes = new Uint8Array(pcm16);
      const len = bytes.byteLength;
      for (let i = 0; i < len; i++) {
        binary += String.fromCharCode(bytes[i]);
      }
      const base64 = btoa(binary);

      sessionPromise.then(session => {
        session.sendRealtimeInput({
          media: {
            mimeType: 'audio/pcm;rate=16000',
            data: base64
          }
        });
        // Reset timer when user speaks
        this.resetCallEndTimer();
      });
    };

    this.source.connect(this.processor);
    this.processor.connect(this.inputContext.destination);
  }

  private async handleMessage(message: LiveServerMessage, sessionPromise: Promise<any>) {
    // 0. Handle Text Transcription (for detecting call end)
    const transcription = message.serverContent?.modelTurn?.parts?.find((part: any) => part.text)?.text ||
                         (message.serverContent as any)?.parts?.find((part: any) => part.text)?.text;
    
    if (transcription) {
      const lowerText = transcription.toLowerCase();
      // Check for call ending phrases
      const endPhrases = ['goodbye', 'good bye', 'have a great day', 'thank you for calling', 'call ended', 'ending the call', 'disconnecting'];
      if (endPhrases.some(phrase => lowerText.includes(phrase))) {
        console.log('Call ending detected from transcription:', transcription);
        // Update status immediately
        this.onStatusChange('disconnected');
        // Wait a moment for the audio to finish, then disconnect
        setTimeout(() => {
          this.disconnect();
        }, 2000);
      }
    }
    
    // 1. Handle Audio Output
    // Check multiple possible paths for audio data
    const audioData = 
      message.serverContent?.modelTurn?.parts?.[0]?.inlineData?.data ||
      (message.serverContent as any)?.parts?.[0]?.inlineData?.data;
    
    if (audioData && this.outputContext) {
      try {
        // Resume audio context if suspended (required for autoplay policy)
        if (this.outputContext.state === 'suspended') {
          console.log('Resuming suspended audio context');
          await this.outputContext.resume();
        }
        
        this.onStatusChange('speaking');
        const audioBuffer = await this.decodeAudio(audioData, this.outputContext);
        
        if (!audioBuffer || audioBuffer.length === 0) {
          console.warn('Empty audio buffer received');
          this.onStatusChange('listening');
          return;
        }
        
        this.nextStartTime = Math.max(this.outputContext.currentTime, this.nextStartTime);
        const source = this.outputContext.createBufferSource();
        source.buffer = audioBuffer;
        source.connect(this.outputContext.destination);
        
        // Add to active sources for interruption support
        this.activeAudioSources.push(source);
        
        source.start(this.nextStartTime);
        this.nextStartTime += audioBuffer.duration;
        
        // Log audio playback for debugging
        console.log('Playing audio:', {
          duration: audioBuffer.duration,
          sampleRate: audioBuffer.sampleRate,
          contextState: this.outputContext.state,
          bufferLength: audioBuffer.length
        });
        
        source.onended = () => {
          // Remove from active sources
          const index = this.activeAudioSources.indexOf(source);
          if (index > -1) {
            this.activeAudioSources.splice(index, 1);
          }
          
          if (this.outputContext && this.outputContext.currentTime >= this.nextStartTime) {
            this.onStatusChange('listening');
            // Reset call end timer when agent finishes speaking
            this.resetCallEndTimer();
          }
        };
      } catch (error) {
        console.error('Error playing audio:', error);
        this.onStatusChange('listening');
      }
    } else {
      // Log when audio data is missing
      if (!audioData) {
        console.log('No audio data in message:', message);
      }
      if (!this.outputContext) {
        console.warn('Output audio context is null');
      }
    }

    // 2. Handle Tool Calls
    if (message.toolCall?.functionCalls) {
      this.onStatusChange('thinking');
      const functionResponses: Array<{ id: string; name: string; response: { result: any } }> = [];
      
      for (const call of message.toolCall.functionCalls) {
        if (!call.name || !call.id) continue;
        
        console.log('Tool Call:', call.name, call.args);
        this.onToolCall(call.name, call.args);
        
        // Use shared tool logic
        const { result, log } = await runTool(call.name, call.args);
        this.onLogMessage('system', log);

        functionResponses.push({
          id: call.id,
          name: call.name,
          response: { result }
        });
      }

      sessionPromise.then(session => {
        session.sendToolResponse({ functionResponses });
      });
    }
  }

  private async decodeAudio(base64: string, ctx: AudioContext): Promise<AudioBuffer> {
    const uint8Array = base64ToUint8Array(base64);
    const int16Array = new Int16Array(uint8Array.buffer);
    const float32Array = new Float32Array(int16Array.length);
    
    for (let i = 0; i < int16Array.length; i++) {
      float32Array[i] = int16Array[i] / 32768.0;
    }

    const buffer = ctx.createBuffer(1, float32Array.length, 24000);
    buffer.getChannelData(0).set(float32Array);
    return buffer;
  }

  // Interrupt current audio playback
  private interruptAudio() {
    console.log('Interrupting audio playback');
    this.activeAudioSources.forEach(source => {
      try {
        source.stop();
      } catch (e) {
        // Source may already be stopped
      }
    });
    this.activeAudioSources = [];
    this.nextStartTime = 0;
    this.onStatusChange('listening');
  }

  // Reset call end timer (call ends after 30 seconds of inactivity)
  private resetCallEndTimer() {
    this.clearCallEndTimer();
    this.callEndTimer = setTimeout(() => {
      console.log('Call timeout - ending call due to inactivity');
      this.disconnect();
    }, 30000); // 30 seconds of inactivity
  }

  // Clear call end timer
  private clearCallEndTimer() {
    if (this.callEndTimer) {
      clearTimeout(this.callEndTimer);
      this.callEndTimer = null;
    }
  }

  async disconnect() {
    console.log('Disconnect called, isConnected:', this.isConnected);
    
    // Always perform cleanup, even if already disconnected
    const wasConnected = this.isConnected;
    this.isConnected = false;
    
    // Clear timers
    this.clearCallEndTimer();
    
    // Stop all active audio sources
    this.interruptAudio();
    
    // Clean up audio processing
    if (this.processor) {
      try {
        this.processor.disconnect();
      } catch (e) {
        console.error('Error disconnecting processor:', e);
      }
      this.processor = null;
    }
    if (this.source) {
      try {
        this.source.disconnect();
      } catch (e) {
        console.error('Error disconnecting source:', e);
      }
      this.source = null;
    }
    
    // Close audio contexts
    if (this.inputContext) {
      try {
        this.inputContext.close();
      } catch (e) {
        console.error('Error closing input context:', e);
      }
      this.inputContext = null;
    }
    if (this.outputContext) {
      try {
        this.outputContext.close();
      } catch (e) {
        console.error('Error closing output context:', e);
      }
      this.outputContext = null;
    }
    
    // Stop media stream
    if (this.stream) {
      this.stream.getTracks().forEach(track => {
        try {
          track.stop();
        } catch (e) {
          console.error('Error stopping track:', e);
        }
      });
      this.stream = null;
    }
    
    // Close session - try multiple methods
    if (this.session) {
      try {
        // Try to close the session
        if (typeof this.session.close === 'function') {
          await this.session.close();
        } else if (typeof this.session.disconnect === 'function') {
          await this.session.disconnect();
        }
        console.log('Session closed');
      } catch (e) {
        console.error('Error closing session:', e);
      }
      this.session = null;
    }
    
    // Update status - always update to ensure UI reflects disconnected state
    this.onStatusChange('disconnected');
    if (wasConnected) {
      this.onLogMessage('system', 'Disconnected.');
    }
    
    console.log('Disconnect completed');
  }
}

export const geminiClient = new GeminiLiveClient();
